import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HeartHandshake, Stethoscope, GraduationCap, Pill, Calculator, TestTube, Scan, HandHeart } from "lucide-react";
import { api } from "../lib/api";

const ROLES = [
  { icon: HeartHandshake, title: "Volunteer", desc: "Drive field projects, fundraisers, events." },
  { icon: GraduationCap, title: "Teacher", desc: "Lead classrooms in remote learning centres." },
  { icon: Stethoscope, title: "Doctor", desc: "Run weekly medical camps and tele-consults." },
  { icon: HandHeart, title: "Nurse", desc: "Support patient care in mobile clinics." },
  { icon: Scan, title: "Radiologist", desc: "Part-time diagnostic reporting." },
  { icon: TestTube, title: "Lab Technician", desc: "Staff diagnostic camps across districts." },
  { icon: Pill, title: "Pharmacology", desc: "Medicine dispensing & stock management." },
  { icon: Calculator, title: "Accountant", desc: "Donor finance reporting & compliance." },
];

export default function Career() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", experience_years: 0, cover_letter: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/career/apply", { ...form, experience_years: Number(form.experience_years) || 0 });
      toast.success("Application received! We'll review and reach out.");
      setForm({ name: "", email: "", phone: "", role: "", experience_years: 0, cover_letter: "" });
    } catch {
      toast.error("Submission failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-3xl mb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#138808] font-bold mb-2">Careers</div>
        <h1 className="serif text-4xl md:text-5xl font-semibold text-[#000080] leading-tight">Work where your work <em className="text-[#FF9933] not-italic">matters</em>.</h1>
        <p className="mt-5 text-lg text-[#4A4A4A]">Join a team where every spreadsheet, every stitch, every stethoscope translates into a child's better morning.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
        {ROLES.map((r, i) => (
          <div key={i} className="card-hover bg-white rounded-2xl border border-[#eae5d9] p-6" data-testid={`role-${i}`}>
            <r.icon className="w-6 h-6 text-[#FF9933] mb-3" strokeWidth={1.6} />
            <div className="serif text-lg font-semibold text-[#000080]">{r.title}</div>
            <div className="text-sm text-[#737373] mt-1">{r.desc}</div>
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-[#eae5d9] p-8 md:p-10">
        <h2 className="serif text-3xl font-semibold text-[#000080] mb-2">Apply Now</h2>
        <p className="text-sm text-[#737373] mb-6">Tell us a little about yourself and the role you're drawn to.</p>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="career-form">
          <Input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="career-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="career-email" />
          <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="career-phone" />
          <Input type="number" min="0" placeholder="Years of Experience" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} data-testid="career-exp" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} required>
            <SelectTrigger data-testid="career-role" className="md:col-span-2"><SelectValue placeholder="Select Role" /></SelectTrigger>
            <SelectContent>
              {ROLES.map(r => <SelectItem key={r.title} value={r.title}>{r.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea placeholder="Cover letter / why you'd be great" value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="md:col-span-2 min-h-[120px]" data-testid="career-cover" />
          <Button type="submit" disabled={loading} className="md:col-span-2 bg-[#FF9933] hover:bg-[#E68A2E] text-white h-12 rounded-full btn-glow" data-testid="career-submit">
            {loading ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </div>
    </div>
  );
}
